import React, { useCallback, useEffect, useRef, useState } from 'react';
import type { InputRef, TableColumnType } from 'antd';
import {
  Button,
  Card,
  Grid,
  Input,
  message,
  Space,
  Table,
  Typography,
} from 'antd';
import type { InternshipProcessWithNames } from '../Types/interfaces';
import {
  ExportCollaborationPackage,
  ExportPackageByProcessId,
  GetEvaluationReportById,
  GetInternshipProcessesOfUniversitySupervisor,
} from '../helper/apiHelper';
import type { FilterDropdownProps } from 'antd/es/table/interface';
import { DownloadOutlined, SearchOutlined } from '@ant-design/icons';
import Highlighter from 'react-highlight-words';
import { useTranslation } from 'react-i18next';
import type { JSX } from 'react';
import { useCurrentUser } from '../hooks/useCurrentUser';

const { useBreakpoint } = Grid;

interface DataType {
  key: React.Key;
  name: string;
  convetion: string;
  workreport: string;
  evaluationreport: string;
  researchagreement: string;
  allDocuments: number;
}

interface DocumentsListProps {
  containsCollaborationFiles: boolean;
}

const DocumentsList = ({
  containsCollaborationFiles,
}: DocumentsListProps): JSX.Element => {
  const { t } = useTranslation();
  const loggedInUser = useCurrentUser();
  const [processes, setProcesses] = useState<InternshipProcessWithNames[]>([]);
  const [data, setData] = useState<DataType[]>([]);
  const [searchText, setSearchText] = useState('');
  const [searchedColumn, setSearchedColumn] = useState('');
  const [selectedRowKeys, setSelectedRowKeys] = useState<React.Key[]>([]);
  const searchInput = useRef<InputRef>(null);
  type DataIndex = keyof DataType;
  const screens = useBreakpoint();

  const { Column } = Table;

  const getEvaluationReportUrl = useCallback(async (reportId: number) => {
    const response = await GetEvaluationReportById(reportId);
    return response.evaluationDocumentUrl;
  }, []);

  const getAllDocumentsUrl = useCallback(async (processIds: number[]) => {
    const response = await ExportPackageByProcessId(processIds);
    return response;
  }, []);

  const getAllCollaborationsUrl = useCallback(async () => {
    const response = await ExportCollaborationPackage();
    return response;
  }, []);

  const handleDownloadAll = async (processIds: number[]): Promise<void> => {
    const response = await getAllDocumentsUrl(processIds);
    const blob = new Blob([response], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `documents.zip`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleDownloadCollaborations = async (): Promise<void> => {
    const response = await getAllCollaborationsUrl();
    const blob = new Blob([response], { type: 'application/zip' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `collaborations.zip`;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  function handleSearch(
    selectedKeys: string[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void {
    confirm();
    setSearchText(selectedKeys[0]);
    setSearchedColumn(dataIndex);
  }

  const handleReset = (clearFilters: () => void): void => {
    clearFilters();
    setSearchText('');
  };

  // Extracted handlers to avoid deep nesting
  const handleInputKeyDown = (
    e: React.KeyboardEvent<HTMLButtonElement>
  ): void => {
    e.stopPropagation();
  };

  const handleInputChange = (
    e: React.ChangeEvent<HTMLInputElement>,
    setSelectedKeys: (selectedKeys: React.Key[]) => void
  ): void => {
    setSelectedKeys(e.target.value ? [e.target.value] : []);
  };

  const handleInputPressEnter = (
    selectedKeys: React.Key[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void => {
    handleSearch(selectedKeys as string[], confirm, dataIndex);
  };

  const handleSearchClick = (
    selectedKeys: React.Key[],
    confirm: FilterDropdownProps['confirm'],
    dataIndex: DataIndex
  ): void => {
    handleSearch(selectedKeys as string[], confirm, dataIndex);
  };

  const handleResetClick = (clearFilters: () => void): void => {
    if (clearFilters) handleReset(clearFilters);
  };

  const handleFilterClick = (
    confirm: FilterDropdownProps['confirm'],
    selectedKeys: React.Key[],
    dataIndex: DataIndex
  ): void => {
    confirm({ closeDropdown: false });
    setSearchText((selectedKeys as string[])[0]);
    setSearchedColumn(dataIndex);
  };

  const handleCloseClick = (close: () => void): void => {
    close();
  };

  const getColumnSearchProps = (
    dataIndex: DataIndex
  ): TableColumnType<DataType> => ({
    filterDropdown: ({
      setSelectedKeys,
      selectedKeys,
      confirm,
      clearFilters,
      close,
    }) => (
      <button
        type="button"
        onKeyDown={handleInputKeyDown}
        style={{ border: 'none' }}
      >
        <div style={{ padding: 8 }}>
          <Input
            ref={searchInput}
            placeholder={t('convSignComp.searchStudent')}
            value={selectedKeys[0]}
            onChange={(e) => handleInputChange(e, setSelectedKeys)}
            onPressEnter={() =>
              handleInputPressEnter(selectedKeys, confirm, dataIndex)
            }
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Button
              type="primary"
              onClick={() =>
                handleSearchClick(selectedKeys, confirm, dataIndex)
              }
              icon={<SearchOutlined />}
              size="small"
              style={{ width: 90 }}
            >
              {t('convSignComp.search')}
            </Button>
            <Button
              onClick={() => handleResetClick(clearFilters ?? ((): void => {}))}
              size="small"
              style={{ width: 90 }}
            >
              {t('convSignComp.reset')}
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() =>
                handleFilterClick(confirm, selectedKeys, dataIndex)
              }
            >
              {t('convSignComp.filter')}
            </Button>
            <Button
              type="link"
              size="small"
              onClick={() => handleCloseClick(close)}
            >
              {t('convSignComp.close')}
            </Button>
          </Space>
        </div>
      </button>
    ),
    filterIcon: (filtered: boolean) => (
      <SearchOutlined style={{ color: filtered ? '#1677ff' : undefined }} />
    ),
    onFilter: (value, record) =>
      record[dataIndex]
        ? record[dataIndex]
            .toString()
            .toLowerCase()
            .includes(value.toString().toLowerCase())
        : false,
    render: (text) =>
      searchedColumn === dataIndex ? (
        <Highlighter
          highlightStyle={{
            backgroundColor: '#ffc069',
            padding: 0,
          }}
          searchWords={[searchText]}
          autoEscape
          textToHighlight={text ? text.toString() : ''}
        />
      ) : (
        text
      ),
  });

  const rowSelection = {
    selectedRowKeys,
    onChange: (selectedKeys: React.Key[]): void => {
      setSelectedRowKeys(selectedKeys);
      console.log('Selected Row Keys:', selectedKeys);
    },
  };

  useEffect(() => {
    const getStudentsOfSupervisor = async (): Promise<void> => {
      const processesResp = await GetInternshipProcessesOfUniversitySupervisor(
        loggedInUser.id
      );
      setProcesses(processesResp);
    };
    getStudentsOfSupervisor();
  }, [loggedInUser.id]);

  useEffect(() => {
    if (!processes) {
      return;
    }
    const fetchEvaluationReportUrls = async (): Promise<void> => {
      if (processes.length > 0) {
        const newData = await Promise.all(
          processes.map((process) => mapProcessToData(process))
        );
        setData(newData);
      }
    };

    const mapProcessToData = async (
      process: InternshipProcessWithNames
    ): Promise<DataType> => {
      const evaluationReportUrl = process.evaluationReportId
        ? await getEvaluationReportUrl(process.evaluationReportId).catch(
            () => ''
          )
        : '';

      return {
        key: process.id,
        name: process.studentName,
        convetion: process.conventionDocumentUrl,
        workreport: process.workLogDocumentUrl,
        evaluationreport: evaluationReportUrl,
        researchagreement: process.researchAgreementDocumentUrl,
        allDocuments: process.id,
      };
    };

    fetchEvaluationReportUrls();
  }, [processes, getEvaluationReportUrl]);

  return (
    <>
      <Card>
        <div
          style={{
            display: 'flex',
            flexDirection: screens.lg ? 'row' : 'column',
            alignItems: 'center',
          }}
        >
          <Typography
            style={{
              border: '1px solid #d9d9d9',
              padding: '4px 8px',
              borderRadius: 4,
            }}
          >
            {t('listDocumentsComp.selected')} {selectedRowKeys.length}
          </Typography>
          <Button
            type="primary"
            onClick={() => {
              if (selectedRowKeys.length === 0) {
                message.warning(t('listDocumentsComp.noRowsSelected'));
                return;
              }
              handleDownloadAll(selectedRowKeys as number[]);
            }}
            style={{ marginLeft: 16 }}
          >
            {t('listDocumentsComp.downloadSelected')}
          </Button>
        </div>
        {containsCollaborationFiles && (
          <Button
            type="primary"
            onClick={() => handleDownloadCollaborations()}
            style={{ marginTop: 16 }}
          >
            {t('listDocumentsComp.downloadCollaborations')}
          </Button>
        )}
      </Card>

      <Table<DataType>
        dataSource={data}
        rowSelection={rowSelection} // Add row selection
        scroll={{ x: 'max-content' }}
      >
        <Column
          title={t('listDocumentsComp.name')}
          dataIndex="name"
          key="name"
          {...getColumnSearchProps('name')}
        />
        <Column
          title={t('listDocumentsComp.conv')}
          dataIndex="convetion"
          key="convetion"
          render={(text: string) => (
            <Button
              type="link"
              href={text}
              disabled={text === null || text === ''}
              target="_blank"
              style={{
                backgroundColor:
                  text === null || text === '' ? '#abd9ed' : '#066d99',
                color: 'white',
              }}
            >
              <DownloadOutlined />
              {t('listDocumentsComp.convDownload')}
            </Button>
          )}
        />
        <Column
          title={t('listDocumentsComp.workReport')}
          dataIndex="workreport"
          key="workreport"
          render={(text: string) => (
            <Button
              type="link"
              href={text}
              disabled={text === null || text === ''}
              target="_blank"
              style={{
                backgroundColor:
                  text === null || text === '' ? '#abd9ed' : '#066d99',
                color: 'white',
              }}
            >
              <DownloadOutlined />
              {t('listDocumentsComp.workReportDownload')}
            </Button>
          )}
        />
        <Column
          title={t('listDocumentsComp.evalReport')}
          dataIndex="evaluationreport"
          key="evaluationreport"
          render={(text: string) => (
            <Button
              type="link"
              href={text}
              disabled={text === null || text === ''}
              target="_blank"
              style={{
                backgroundColor:
                  text === null || text === '' ? '#abd9ed' : '#066d99',
                color: 'white',
              }}
            >
              <DownloadOutlined />
              {t('listDocumentsComp.evalReportDownload')}
            </Button>
          )}
        />
        <Column
          title={t('listDocumentsComp.researchAgreement')}
          dataIndex="researchagreement"
          key="researchagreement"
          render={(text: string) => (
            <Button
              type="link"
              href={text}
              disabled={text === null || text === ''}
              target="_blank"
              style={{
                backgroundColor:
                  text === null || text === '' ? '#abd9ed' : '#066d99',
                color: 'white',
              }}
            >
              <DownloadOutlined />
              {t('listDocumentsComp.researchAgreementDownload')}
            </Button>
          )}
        />
        <Column
          title={t('listDocumentsComp.allDocs')}
          dataIndex="allDocuments"
          key="allDocuments"
          render={(processId: number) => (
            <Button
              type="link"
              target="_blank"
              onClick={() => handleDownloadAll([processId])}
              style={{
                backgroundColor: '#066d99',
                color: 'white',
              }}
            >
              <DownloadOutlined />
              {t('listDocumentsComp.downloadAll')}
            </Button>
          )}
        />
      </Table>
    </>
  );
};

export default DocumentsList;
